<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { PORTAL_PREVIEW_STUB_ID } from '../lib/previewEventStub';
import { stripPortalScripts } from '../lib/stripPortalScripts';

const props = defineProps({
  portal: { type: Object, required: true },
});

const emit = defineEmits([
  'open',
  'settings',
  'duplicate',
  'delete',
  'preview',
]);

const store = useStore();
const { t } = useI18n();
const [showMenu, toggleMenu] = useToggle(false);

const previewHtml = ref('');
const previewLoading = ref(false);

const hasPagePreview = computed(() => Boolean(props.portal.page_preview_url));

const menuItems = computed(() => [
  {
    label: t('PLANNING.EVENT_PORTALS.ACTIONS.SETTINGS'),
    value: 'settings',
    action: 'settings',
    icon: 'i-lucide-settings',
  },
  {
    label: t('PLANNING.EVENT_PORTALS.ACTIONS.DUPLICATE'),
    value: 'duplicate',
    action: 'duplicate',
    icon: 'i-lucide-copy',
  },
  {
    label: t('PLANNING.EVENT_PORTALS.ACTIONS.GENERATE_PREVIEW'),
    value: 'preview',
    action: 'preview',
    icon: 'i-lucide-image',
  },
  {
    label: t('PLANNING.EVENT_PORTALS.ACTIONS.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash-2',
  },
]);

const handleMenuAction = ({ action }) => {
  toggleMenu(false);
  if (action === 'settings') emit('settings', props.portal);
  if (action === 'duplicate') emit('duplicate', props.portal.id);
  if (action === 'delete') emit('delete', props.portal.id);
  if (action === 'preview') emit('preview', props.portal.id);
};

const loadLivePreview = async () => {
  if (hasPagePreview.value) return;
  previewLoading.value = true;
  try {
    const data = await store.dispatch('planningEventPortals/preview', {
      templateId: props.portal.id,
      eventId: PORTAL_PREVIEW_STUB_ID,
      shell: true,
    });
    previewHtml.value = stripPortalScripts(data?.html || '');
  } catch {
    previewHtml.value = '';
  } finally {
    previewLoading.value = false;
  }
};

onMounted(loadLivePreview);
</script>

<template>
  <div
    class="group relative flex flex-col rounded-2xl border border-n-slate-6 bg-n-slate-1 shadow-sm hover:shadow-md hover:border-n-brand-7 transition-all cursor-pointer"
    :class="showMenu ? 'z-20' : 'z-0'"
    role="button"
    tabindex="0"
    @click="emit('open', portal.id)"
    @keydown.enter="emit('open', portal.id)"
  >
    <!-- Tall page-like preview -->
    <div
      class="relative aspect-[9/14] w-full overflow-hidden rounded-t-2xl bg-n-slate-3 border-b border-n-slate-6"
    >
      <!-- Saved page screenshot (settings index only — not OG) -->
      <img
        v-if="hasPagePreview"
        :src="portal.page_preview_url"
        :alt="portal.name"
        class="absolute inset-0 size-full object-cover object-top"
      />

      <!-- Live fallback when no screenshot yet -->
      <div
        v-else-if="previewHtml"
        class="absolute inset-0 overflow-hidden pointer-events-none origin-top-left"
      >
        <iframe
          :srcdoc="previewHtml"
          title="Portal preview"
          class="border-0 bg-white pointer-events-none"
          style="width: 400%; height: 400%; transform: scale(0.25)"
          sandbox="allow-same-origin"
        />
      </div>

      <div
        v-else
        class="absolute inset-0 flex flex-col items-center justify-center gap-2"
      >
        <span
          v-if="previewLoading"
          class="i-lucide-loader-2 size-5 text-n-slate-9 animate-spin"
        />
        <span v-else class="i-lucide-layout-template size-10 text-n-slate-8" />
      </div>

      <span
        v-if="portal.is_default"
        class="absolute top-3 left-3 z-10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-n-brand-9 text-white"
      >
        {{ t('PLANNING.EVENT_PORTALS.DEFAULT_BADGE') }}
      </span>
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-between gap-2 px-4 py-3">
      <div class="min-w-0">
        <p class="text-sm font-semibold text-n-slate-12 truncate">
          {{ portal.name }}
        </p>
        <p v-if="portal.updated_at" class="text-xs text-n-slate-10 mt-0.5">
          {{
            new Date(portal.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          }}
        </p>
      </div>

      <div
        v-on-clickaway="() => toggleMenu(false)"
        class="relative shrink-0"
        @click.stop
      >
        <Button
          icon="i-lucide-ellipsis"
          color="slate"
          variant="ghost"
          size="sm"
          class="rounded-lg"
          @click="toggleMenu()"
        />
        <DropdownMenu
          v-if="showMenu"
          :menu-items="menuItems"
          class="mt-1 ltr:right-0 rtl:left-0 top-full z-50 min-w-44"
          @action="handleMenuAction"
        />
      </div>
    </div>
  </div>
</template>
